import { useEffect, useRef, useState } from "react";
import { useFetcher, useSearchParams } from "react-router";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Globe,
  LayoutDashboard,
  Megaphone,
  PenLine,
  Server,
  ShoppingBag,
  Smartphone,
  Sparkles,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";

import { Button } from "~/components/ui/button";
import { useT } from "~/i18n/context";
import { cn } from "~/lib/utils";
import { track, withoutPrivateQuery } from "~/lib/analytics";
import { subscribeConsent } from "~/lib/consent";
import { AttributionFields, useLeadSubmissionTracking, type InquiryField } from "./lead-tracking";

/**
 * Shared Start a project inquiry flow:
 * choose a service, then share the brief and contact details on one screen.
 * Browser back steps back through
 * the flow (steps live in the URL via ?step=, so back/forward behave like
 * pages; on the first step, back leaves to the previous page as normal).
 *
 * The menu speaks customer language: broad "what are you building"
 * categories (business-owner words, not our service taxonomy) with a short
 * refine question where a category covers several services. Every path
 * resolves to a service slug. Scope and pricing are discussed after the inquiry.
 */

type Refinement = { id: string; slug: string };
type Category = {
  id: string;
  icon: LucideIcon;
  /** Direct service slug when the category needs no refine step. */
  slug?: string;
  refinements?: Refinement[];
};

const CATEGORIES: Category[] = [
  { id: "design", icon: PenLine, slug: "design" },
  {
    id: "website",
    icon: Globe,
    slug: "web",
    // Keep older service links specific without asking another website question.
    refinements: [
      { id: "new", slug: "web" },
      { id: "immersive", slug: "immersive" },
      { id: "traffic", slug: "seo" },
      { id: "restaurants", slug: "restaurants" },
      { id: "roofing", slug: "roofing" },
    ],
  },
  { id: "store", icon: ShoppingBag, slug: "ecommerce" },
  { id: "realEstate", icon: Building2, slug: "real-estate" },
  { id: "app", icon: Smartphone, slug: "mobile" },
  {
    id: "ai",
    icon: Sparkles,
    refinements: [
      { id: "assistant", slug: "ai-agents" },
      { id: "features", slug: "ai" },
    ],
  },
  {
    id: "tools",
    icon: LayoutDashboard,
    refinements: [
      { id: "dashboard", slug: "data-viz" },
      { id: "realtime", slug: "realtime" },
      { id: "connect", slug: "integrations" },
    ],
  },
  {
    id: "hosting",
    icon: Server,
    refinements: [
      { id: "setup", slug: "cloud" },
      { id: "deploys", slug: "devops" },
      { id: "support", slug: "managed-hosting" },
    ],
  },
  { id: "campaigns", icon: Megaphone, slug: "campaigns" },
  { id: "growth", icon: TrendingUp, slug: "business-growth" },
];

const OTHER = "other";
const STEPS = ["service", "refine", "brief", "contact"] as const;
type Step = (typeof STEPS)[number];
const INQUIRY_FIELDS: readonly InquiryField[] = ["description", "name", "email", "company", "phone", "website"];

function inquiryField(target: EventTarget): HTMLInputElement | HTMLTextAreaElement | null {
  return (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)
    && INQUIRY_FIELDS.includes(target.name as InquiryField) ? target : null;
}

export function selectionForService(slug: string | null) {
  if (slug === OTHER) return { category: OTHER, refinement: null };
  for (const category of CATEGORIES) {
    if (category.slug === slug) return { category: category.id, refinement: null };
    const refinement = category.refinements?.find((item) => item.slug === slug);
    if (refinement) return { category: category.id, refinement: refinement.id };
  }
  return null;
}

const optionCard = (selected: boolean) =>
  cn(
    "flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-[border-color,transform] duration-150 active:scale-[0.98]",
    "outline-none focus-visible:ring-2 focus-visible:ring-accent-brand",
    selected
      ? "border-accent-brand bg-accent-brand/10"
      : "border-border/60 bg-surface-elevated hover:border-accent-brand/50",
  );

export function EstimateWizard() {
  const t = useT();
  const fetcher = useFetcher<{
    ok: boolean;
    error?: string;
    conversionId?: string;
    failure_code?: string;
  }>();
  const [params, setParams] = useSearchParams();
  const [brief, setBrief] = useState(() => params.get("brief") ?? "");
  const [contact, setContact] = useState({ name: "", email: "", company: "", phone: "", website: "" });
  const [submissionId, setSubmissionId] = useState("");
  useEffect(() => { setSubmissionId(crypto.randomUUID()); }, []);
  const [measurementAllowed, setMeasurementAllowed] = useState(false);

  // Only non-sensitive choices live in the URL. The brief stays in component
  // state, which survives browser back/forward between the wizard steps.
  const prefill = params.has("cat") ? null : selectionForService(params.get("service"));
  const category = params.get("cat") ?? prefill?.category ?? null;
  const refinement = params.get("ref") ?? prefill?.refinement ?? null;
  const rawStep = (params.get("step") === "size" ? "brief" : params.get("step")) as Step | null;

  const cat = CATEGORIES.find((c) => c.id === category) ?? null;
  const selectedRefinement = cat?.refinements?.find(item => item.id === refinement);
  const isOther = category === OTHER;
  const needsRefine = Boolean(cat?.refinements && !cat.slug);
  const slug = isOther
    ? OTHER
    : selectedRefinement?.slug ??
      cat?.slug ??
      null;
  const submission = useLeadSubmissionTracking("estimate", fetcher.data, slug ?? undefined);

  // Clamp the step to what the collected answers actually support — a stale
  // or hand-edited URL can't skip questions.
  let step: Step = prefill ? "brief" : "service";
  if (rawStep && STEPS.includes(rawStep)) step = rawStep;
  if (step !== "service") {
    if (!cat && !isOther) step = "service";
    else if (step === "refine" && !needsRefine) step = "brief";
    else if ((step === "brief" || step === "contact") && !slug)
      step = needsRefine ? "refine" : "service";
  }
  // Old contact/size links land on the same combined details screen.
  if (step === "contact") step = "brief";

  const go = (next: Record<string, string | null>, replace = false) => {
    const p = withoutPrivateQuery(params);
    p.delete("brieferr");
    p.delete("size");
    if (prefill) {
      p.set("cat", prefill.category);
      if (prefill.refinement) p.set("ref", prefill.refinement);
    }
    p.delete("service");
    for (const [k, v] of Object.entries(next)) {
      if (v === null) p.delete(k);
      else p.set(k, v);
    }
    setParams(p, { preventScrollReset: true, replace });
  };

  useEffect(() => {
    const clean = withoutPrivateQuery(params);
    clean.delete("size");
    if (["size", "contact"].includes(clean.get("step") ?? "") || (clean.get("step") === "refine" && step === "brief")) clean.set("step", "brief");
    if (clean.toString() === params.toString()) return;
    setParams(clean, { replace: true, preventScrollReset: true });
  }, [params, setParams, step]);

  useEffect(() => subscribeConsent(decision => setMeasurementAllowed(decision === "granted")), []);

  useEffect(() => {
    // Measure the current step after opt-in, without replaying earlier steps.
    if (measurementAllowed) track("estimate_step", { step, service: slug ?? "unspecified" });
  }, [measurementAllowed, step, slug]);

  const summaryLabel = isOther
    ? t("estimateWizard.categories.other.label")
    : category
      ? t(`estimateWizard.categories.${category}.label`)
      : "";

  const submittedEmail = useRef("");
  const businessDetails = useRef<HTMLDetailsElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, [step, fetcher.data?.ok]);

  const isSubmitting = fetcher.state !== "idle";
  const isSent = fetcher.data?.ok === true;
  const sendError =
    fetcher.data && fetcher.data.ok === false ? fetcher.data.error : null;

  useEffect(() => {
    if (sendError && businessDetails.current && (contact.company || contact.website || contact.phone)) {
      businessDetails.current.open = true;
    }
  }, [sendError, contact.company, contact.website, contact.phone]);

  if (isSent) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-12 md:py-32">
        <div className="rounded-2xl border border-border/60 bg-surface-elevated p-6 md:p-12">
          <h1 ref={headingRef} tabIndex={-1} className="font-display text-4xl font-semibold tracking-display outline-none md:text-5xl">
            {t("estimateWizard.done.title")}
          </h1>
          <p className="mt-4 break-words text-base leading-relaxed text-muted-foreground">
            {t("estimateWizard.done.body", { email: submittedEmail.current })}
          </p>
        </div>
      </div>
    );
  }

  const heading = (key: string) => (
    <h1
      ref={headingRef}
      tabIndex={-1}
      className="font-display text-4xl font-semibold tracking-display outline-none md:text-5xl"
    >
      {t(key)}
    </h1>
  );

  const backButton = step === "refine" && (
    <button
      type="button"
      onClick={() => go({ step: "service" })}
      className="inline-flex min-h-11 items-center gap-2 rounded-md font-sans text-sm normal-case tracking-normal text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-accent-brand"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      {t("estimateWizard.back")}
    </button>
  );

  return (
    <div className="mx-auto max-w-3xl px-5 py-6 md:px-6 md:py-12" onFocusCapture={event => {
      if (event.target instanceof HTMLElement && event.target.matches("input, textarea, select, button")) submission.onStart();
    }}>
      {backButton}

      {category && step !== "service" && (
        <div
          data-testid="wizard-range"
          className="flex items-center justify-between gap-3 border-b border-border/60 pb-3"
        >
          <div className="min-w-0 text-sm text-muted-foreground">
            <span>{summaryLabel}{cat && selectedRefinement ? ` · ${t(`estimateWizard.refine.${cat.id}.options.${selectedRefinement.id}.label`)}` : ""}</span>
          </div>
          <button type="button" onClick={() => go({ step: "service" })} className="min-h-11 shrink-0 rounded-sm px-2 text-sm text-foreground transition-opacity hover:opacity-75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">{t("estimateWizard.change")}</button>
        </div>
      )}

      <div key={step} className={cn("nav-tile-in", step !== "service" && "mt-6")}>
        {step === "service" && (
          <>
            {heading("estimateWizard.service.title")}
            <div
              role="radiogroup"
              aria-label={t("estimateWizard.service.title")}
              className="mt-6 grid gap-3 sm:grid-cols-2 md:mt-8"
            >
              <button
                type="button"
                role="radio"
                aria-checked={isOther}
                onClick={() => go({ cat: OTHER, ref: null, step: "brief" })}
                className={cn(optionCard(isOther), "justify-between border-foreground/25 sm:col-span-2")}
              >
                <span className="min-w-0">
                  <span className="block text-base font-medium">
                    {t("estimateWizard.categories.other.label")}
                  </span>
                  <span className="mt-0.5 block text-sm text-muted-foreground">
                    {t("estimateWizard.categories.other.hint")}
                  </span>
                </span>
                <ArrowRight className="h-5 w-5 shrink-0" aria-hidden />
              </button>
              {CATEGORIES.map((c) => {
                const Icon = c.icon;
                return (
                  <button
                    key={c.id}
                    type="button"
                    role="radio"
                    aria-checked={category === c.id}
                    onClick={() =>
                      go({
                        cat: c.id,
                        ref: null,
                        step: c.refinements && !c.slug ? "refine" : "brief",
                      })
                    }
                    className={optionCard(category === c.id)}
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-background text-foreground/80">
                      <Icon className="h-5 w-5" aria-hidden />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-base font-medium">
                        {t(`estimateWizard.categories.${c.id}.label`)}
                      </span>
                      <span className="mt-0.5 block text-sm text-muted-foreground">
                        {t(`estimateWizard.categories.${c.id}.hint`)}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {step === "refine" && cat?.refinements && (
          <>
            {heading(`estimateWizard.refine.${cat.id}.title`)}
            <div
              role="radiogroup"
              aria-label={t(`estimateWizard.refine.${cat.id}.title`)}
              className="mt-6 grid gap-3 md:mt-8"
            >
              {cat.refinements.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  role="radio"
                  aria-checked={refinement === r.id}
                  onClick={() => go({ ref: r.id, step: "brief" })}
                  className={optionCard(refinement === r.id)}
                >
                  <span className="min-w-0">
                    <span className="block text-base font-medium">
                      {t(`estimateWizard.refine.${cat.id}.options.${r.id}.label`)}
                    </span>
                    <span className="mt-0.5 block text-sm text-muted-foreground">
                      {t(`estimateWizard.refine.${cat.id}.options.${r.id}.hint`)}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {step === "brief" && (
          <fetcher.Form
            method="post"
            onInputCapture={event => {
              if (inquiryField(event.target)) submission.onFieldEdit();
            }}
            onInvalidCapture={event => {
              const field = inquiryField(event.target);
              // Native validation does not call onSubmit. Each invalid control
              // emits an event; the first one represents this blocked attempt.
              const firstInvalid = [...event.currentTarget.elements].find(element => {
                const candidate = inquiryField(element);
                return candidate?.willValidate && !candidate.validity.valid;
              });
              if (!field || field !== firstInvalid) return;
              submission.onSubmitAttempt();
              const code = field.validity.valueMissing ? "value_missing"
                : field.validity.typeMismatch ? "type_mismatch"
                : field.validity.tooLong ? "too_long" : "invalid";
              submission.onValidationFailed(field.name as InquiryField, code);
            }}
            onSubmit={(e) => {
              const form = e.currentTarget;
              submission.onSubmitAttempt();
              const name = (
                form.elements.namedItem("name") as HTMLInputElement
              )?.value.trim();
              const email = (
                form.elements.namedItem("email") as HTMLInputElement
              )?.value.trim();
              if (!name || !email) {
                e.preventDefault();
                form.dataset.error = "1";
                submission.onValidationFailed(!name ? "name" : "email", "value_missing");
              } else {
                delete form.dataset.error;
                submittedEmail.current = email;
              }
            }}
            className="group/form"
          >
            <AttributionFields />
            {heading("estimateWizard.brief.title")}
            <p className="mt-3 text-sm text-muted-foreground">
              {t("estimateWizard.contact.body")}
            </p>

            <input type="hidden" name="submissionId" value={submissionId} />
            <input type="hidden" name="service" value={slug ?? ""} />
            <div hidden aria-hidden="true"><label>Fax<input name="fax_number" tabIndex={-1} autoComplete="off" /></label></div>
            <label htmlFor="project-description" className="mb-2 mt-6 block text-sm font-medium">{t("estimateWizard.contact.description")}</label>
            <textarea id="project-description" name="description" required maxLength={4000} rows={4}
              value={brief} onChange={event => setBrief(event.target.value)} placeholder={t("estimateWizard.brief.placeholder")}
              className="w-full rounded-xl border border-border/60 bg-surface-elevated p-4 text-base leading-relaxed outline-none focus-visible:ring-2 focus-visible:ring-accent-brand" />

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="project-name" className="mb-2 block text-sm font-medium">{t("estimateWizard.contact.name")}</label>
                <input
                  id="project-name"
                  name="name"
                  value={contact.name}
                  onChange={event => setContact({ ...contact, name: event.target.value })}
                  autoComplete="name"
                  required
                  maxLength={120}
                  className="w-full rounded-xl border border-border/60 bg-surface-elevated px-4 py-3 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-accent-brand md:px-5 md:py-4"
                />
              </div>
              <div>
                <label htmlFor="project-email" className="mb-2 block text-sm font-medium">{t("estimateWizard.contact.email")}</label>
                <input
                  id="project-email"
                  name="email"
                  value={contact.email}
                  onChange={event => setContact({ ...contact, email: event.target.value })}
                  type="email"
                  autoComplete="email"
                  required
                  maxLength={254}
                  className="w-full rounded-xl border border-border/60 bg-surface-elevated px-4 py-3 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-accent-brand md:px-5 md:py-4"
                />
              </div>
            </div>
            <details ref={businessDetails} className="mt-5">
              <summary className="min-h-11 cursor-pointer rounded-md py-3 text-sm text-muted-foreground hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-brand">
                {t("estimateWizard.contact.businessDetails")}
              </summary>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="project-company" className="mb-2 block text-sm font-medium">{t("estimateWizard.contact.company")}</label>
                  <input
                    id="project-company"
                    name="company"
                    value={contact.company}
                    onChange={event => setContact({ ...contact, company: event.target.value })}
                    autoComplete="organization"
                    maxLength={200}
                    className="w-full rounded-xl border border-border/60 bg-surface-elevated px-4 py-3 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-accent-brand md:px-5 md:py-4"
                  />
                </div>
                {(["website", "phone"] as const).map(field => (
                  <div key={field}>
                    <label htmlFor={`project-${field}`} className="mb-2 block text-sm font-medium">{t(`estimateWizard.contact.${field}`)}</label>
                    <input id={`project-${field}`} name={field} value={contact[field]}
                      onChange={event => setContact({ ...contact, [field]: event.target.value })}
                      type={field === "phone" ? "tel" : "text"} inputMode={field === "phone" ? "tel" : "url"}
                      autoComplete={field === "phone" ? "tel" : "url"} maxLength={field === "phone" ? 40 : 500}
                      className="w-full rounded-xl border border-border/60 bg-surface-elevated px-4 py-3 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-accent-brand md:px-5 md:py-4" />
                  </div>
                ))}
              </div>
            </details>
            <p className="mt-3 hidden text-sm text-red-400 group-data-[error]/form:block">
              {t("estimateWizard.contact.required")}
            </p>
            {sendError && (
              <p role="alert" className="mt-3 text-sm text-red-400">{sendError}</p>
            )}
            <div className="mt-6">
              <Button className="w-full sm:w-auto" size="lg" type="submit" disabled={isSubmitting || !submissionId}>
                {isSubmitting
                  ? t("estimateWizard.sending")
                  : t("estimateWizard.send")}
              </Button>
            </div>
          </fetcher.Form>
        )}
      </div>
      <noscript><p className="mt-8 text-base">{t("estimateWizard.noScript")} <a href="mailto:support@goatsquadstudios.com">support@goatsquadstudios.com</a></p></noscript>
    </div>
  );
}
